import { GenericService } from 'services/genericService';
import {
	getCorpUsersForNumber,
	getCorpUserDepartmentDataForNumber,
	getCorpUserDetails,
	createCorpUser,
	getCorpUsersForAccounts,
	updateCorpUserInformation,
	updateCorpUserType,
	updateCorpUserPermissions,
	deleteCorpUsers,
	deleteAccountPermissionsForCorpUsers,
	getCurrentUserDetailsRest,
	importUsersFromFile,
	addCorpUserPermissions,
	sendRegisterBusinessAccount,
	resendActivationEmailRestPost,
	requestForOtp,
	deleteCorpUserPermissions,
} from 'utils/api/queries/omantelApi';
import { Observable } from 'rxjs';
import { CorpUsers } from 'utils/api/models/users/corpUsers';
import { Users } from 'utils/api/models/users/users';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { UserProfile } from 'utils/api/models/users/userProfile';
import { CorpUserDetails } from 'utils/api/models/users/corpUserDetails';
import { tap, finalize } from 'rxjs/operators';
import { dispatch } from 'redux/store';
import { setUserProfileData } from 'redux/modules/session/actions';

import * as payloads from 'utils/api/payloads/omantelApi';
import invalidateCache from 'utils/api/invalidateCache';

export class UsersService extends GenericService {
	public static getCorpUsersForNumber(upAccountId: string): Observable<CorpUsers> {
		return getCorpUsersForNumber(upAccountId).call();
	}

	public static getCorpUserDetails(userId: string): Observable<CorpUserDetails> {
		return getCorpUserDetails(userId)
			.withErrorHandling()
			.call();
	}

	public static getCorpUserDepartmentDataForNumber(upAccountId: string): Observable<CorpUserData> {
		return getCorpUserDepartmentDataForNumber(upAccountId)
			.withErrorHandling()
			.call();
	}

	public static getUserProfile(): Observable<UserProfile> {
		return getCurrentUserDetailsRest()
			.withErrorHandling()
			.call()
			.pipe(
				tap((userProfile: UserProfile) => {
					dispatch(setUserProfileData(userProfile));
				})
			);
	}

	public static getCorpUsersForAccounts(): Observable<Users> {
		return getCorpUsersForAccounts()
			.withErrorHandling()
			.call();
	}

	public static createCorpUser(data: payloads.NewUserData): Observable<any> {
		return createCorpUser(data)
			.withErrorHandling()
			.call();
	}

	public static updateCorpUserInformation(
		data: payloads.UpdateUserData,
		isNewDashBoardEnabled = false
	): Observable<any> {
		return isNewDashBoardEnabled
			? updateCorpUserInformation(data)
					.disableCache()
					.call()
					.pipe(finalize(() => this.invalidateUserCache()))
			: updateCorpUserInformation(data)
					.disableCache()
					.call()
					.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static updateCorpUserType(data: payloads.UpdateUserTypeData): Observable<any> {
		return updateCorpUserType(data)
			.disableCache()
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static deleteCorpUsers(userIds: string[]): Observable<any> {
		return deleteCorpUsers(userIds)
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static updateCorpUserPermissions(data: payloads.UpdateUserPermissionsData): Observable<any> {
		return updateCorpUserPermissions(data)
			.disableCache()
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static deleteAccountPermissionsForCorpUsers(data: payloads.DeleteAccountPermissionsData): Observable<any> {
		return deleteAccountPermissionsForCorpUsers(data)
			.withErrorHandling()
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static importUsersFromFile(base64data: string): Observable<any> {
		return importUsersFromFile(base64data)
			.disableCache()
			.setTimeout(120000)
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static addCorpUserPermissions(userId: string, permissions: payloads.PermissionData[]): Observable<any> {
		return addCorpUserPermissions(userId, permissions)
			.withErrorHandling()
			.disableCache()
			.call()
			.pipe(finalize(() => this.invalidateUserCache()));
	}

	public static sendRegisterBusinessAccount(data: payloads.RegisterBusinessAccountData): Observable<any> {
		return sendRegisterBusinessAccount(data)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	public static resendActivationEmailRestPost(userId: string): Observable<any> {
		return resendActivationEmailRestPost(userId)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	public static requestForOtp(
		operationContext: payloads.OperationContextEnum,
		msisdn: string,
		isNewDashBoardEnabled = false
	): Observable<any> {
		return isNewDashBoardEnabled
			? requestForOtp(operationContext, msisdn)
					.disableCache()
					.call()
			: requestForOtp(operationContext, msisdn)
					.withErrorHandling()
					.disableCache()
					.call();
	}

	public static deleteCorpUserPermissions(data: payloads.CorpUserPermissionsData): Observable<any> {
		return deleteCorpUserPermissions(data)
			.withErrorHandling()
			.disableCache()
			.call();
	}

	private static invalidateUserCache() {
		invalidateCache(
			[
				// users
				'getCorpUserDetails',
				'getCorpUsersForAccounts',
				// organizations
				'getCorpAccountDetails',
				'getCorpAccountAdmins',
				'getCorpAccountManagers',
				'getDepartmentsForCurrentUser',
				'getDepartmentDetails',
				// numbers
				'getCorpUserNumbersWithChilds',
				'getNumbersForUser',
				//accounts
				'getCorpAccountsWithAdminsForCurrentUser',
			],
			true
		);
	}
}
